import { getBucket } from "math-utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { secureJsonParse } from "_helpers/utils/jsonParser";

import { useAdaptivePractice } from "./useAdaptivePractice";
import { useFeatureFlags } from "./useFeatureFlags";
import { useProfile } from "./useProfile";

export type TallyForm = {
  nl: string;
  en: string;
};

interface Params {
  productId?: string;
}

export const useTally = () => {
  const { tallyForm } = useFeatureFlags();
  const { i18n } = useTranslation("common");
  const { hasPracticed, fetchHasPracticed } = useAdaptivePractice();
  const { userDetails, survey, fetchSurvey, submitSurvey } = useProfile();
  const { en, nl } = secureJsonParse<TallyForm>(tallyForm as string) || {};
  const tallyFormId = i18n.language === "en" ? en : nl;
  const { productId } = useParams() as unknown as Params;

  useEffect(() => {
    if (!tallyFormId || !userDetails?.hashId) return;

    // `getBucket` will return a value between 0 and 11 (same as `getMonth`)
    const userAssignedBucket = getBucket(userDetails.hashId, 12);

    // Only users who're assigned to the current month will be eligible to see the form
    const currentlyActiveBucket = new Date().getMonth();
    if (userAssignedBucket !== currentlyActiveBucket) return;

    // For eligible users, we need to verify they've practiced
    fetchHasPracticed();
  }, [tallyFormId, userDetails?.hashId]);

  useEffect(() => {
    // If user has practiced, we must check if backend states the user should receive
    // survey (they won't if they have recently answered it, for example)
    if (hasPracticed) {
      fetchSurvey();
    }
  }, [hasPracticed]);

  useEffect(() => {
    // Display survey only for users that meet all the conditions
    if (survey?.shouldReceiveSurvey && tallyFormId) {
      window.Tally.openPopup(tallyFormId, {
        hiddenFields: {
          userId: userDetails?.id,
          organizationId: userDetails?.schoolNumber,
          surveyId: `he-nps-${tallyFormId}`,
          language: i18n.language,
          token: "",
          role: userDetails?.role,
          productId: productId,
        },
        onSubmit: () => submitSurvey(),
      });
    }
  }, [survey?.shouldReceiveSurvey, tallyFormId]);
};
